import React from 'react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { ChevronRight, Bell, Settings, HelpCircle, Sparkles, LogOut } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface MobileNavAuthSectionProps {
  isAuthenticated: boolean;
  isPremium: boolean;
  user?: { name?: string; email?: string; avatar?: string };
  onNavigate: (to: string) => void;
  onSignOut: () => void;
  onAuthAction: () => void;
  onStartTour: () => void;
}

const menuButtonClass = cn(
  'w-full flex items-center space-x-3 p-3 rounded-lg text-left',
  'transition-colors duration-200',
  'hover:bg-neutral-50 dark:hover:bg-neutral-800 focus:outline-none focus:ring-2 focus:ring-warning-500 focus:ring-offset-2 dark:focus:ring-offset-neutral-900'
);

export const MobileNavAuthSection: React.FC<MobileNavAuthSectionProps> = ({
  isAuthenticated,
  isPremium,
  user,
  onNavigate,
  onSignOut,
  onAuthAction,
  onStartTour,
}) => {
  if (!isAuthenticated) {
    return (
      <div className="space-y-3">
        <Button variant="outline" onClick={onAuthAction} className="w-full">
          Sign In
        </Button>
        <Button
          onClick={onAuthAction}
          className="w-full bg-warning-500 hover:bg-warning-600 text-white"
        >
          Get Started
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {isPremium && (
        <Badge className="bg-warning-100 dark:bg-warning-900 text-warning-800 dark:text-warning-200 border-warning-300 dark:border-warning-700">
          Premium Member
        </Badge>
      )}

      <button onClick={() => onNavigate('/account')} className={cn(menuButtonClass, 'group')}>
        <Avatar className="h-8 w-8">
          <AvatarImage src={user?.avatar} alt={user?.name || 'User'} />
          <AvatarFallback>{user?.name?.charAt(0).toUpperCase() || 'U'}</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-foreground group-hover:text-warning-700 dark:group-hover:text-warning-300">
            {user?.name || 'Profile'}
          </div>
          <div className="text-xs text-helper">View account settings</div>
        </div>
        <ChevronRight className="w-4 h-4 text-neutral-400 dark:text-neutral-500 transition-transform duration-200 group-hover:translate-x-1" />
      </button>

      <Separator className="my-2" />

      <button onClick={() => onNavigate('/account?tab=notifications')} className={menuButtonClass}>
        <Bell className="w-5 h-5 text-neutral-400 dark:text-neutral-500" />
        <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
          Notifications
        </span>
      </button>

      <button onClick={() => onNavigate('/settings')} className={menuButtonClass}>
        <Settings className="w-5 h-5 text-neutral-400 dark:text-neutral-500" />
        <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">Settings</span>
      </button>

      <button onClick={onStartTour} className={menuButtonClass}>
        <Sparkles className="w-5 h-5 text-secondary-500" />
        <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
          Quick Start Tour
        </span>
      </button>

      <button onClick={() => onNavigate('/help')} className={menuButtonClass}>
        <HelpCircle className="w-5 h-5 text-neutral-400 dark:text-neutral-500" />
        <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
          Help & Support
        </span>
      </button>

      <Button
        variant="outline"
        onClick={onSignOut}
        className="w-full justify-start text-neutral-700 dark:text-neutral-300 hover:text-error-600 dark:hover:text-error-400 hover:border-error-300 dark:hover:border-error-700"
      >
        <LogOut className="w-4 h-4 mr-2" />
        Sign Out
      </Button>
    </div>
  );
};
